import { IObservable, Subject } from '~/rage/utils';
import {
	ClientError,
	ClientErrorCode,
	HeldItem,
	IClientConfigs,
	InventoryType,
	InventoryExchangeConfirmation,
	Item,
	ItemAction,
	ItemData,
	PackedInventory,
} from '~/shared';
import { BackendService } from '../BackendService';
import { GameConfigService } from '../GameConfigService';
import { INotificationsService } from '../NotificationsService';
import { IPlayerService } from '../PlayerService';
import { IRpcService } from '../RpcService';
import { VarsService } from '../VarsService/VarsService';
import { IInventoryService } from './inventoryServiceTypes';

const PLAYER_INVENTORIES_FOR_PICKUP_NOTIFY: InventoryType[] = [
	InventoryType.EQUIPMENT,
	InventoryType.HOTBAR,
	InventoryType.PLAYER,
	InventoryType.BACKPACK,
];

const PICKUP_BULK_CATALOG_IDS_THRESHOLD = 4;

export class InventoryService implements IInventoryService {
	private inventoryIds: Subject<Record<InventoryType, string>> = new Subject({
		[InventoryType.PLAYER]: '',
		[InventoryType.EQUIPMENT]: '',
		[InventoryType.HOTBAR]: '',
		[InventoryType.BACKPACK]: '',
		[InventoryType.VEHICLE]: '',
		[InventoryType.HOUSE]: '',
		[InventoryType.EXCHANGE]: '',
		[InventoryType.DROPPED]: '',
	});
	private inventoryItems = new Subject<IClientConfigs['inventory']['items']>(
		[],
	);
	private inventories: Record<InventoryType, Subject<PackedInventory | null>> =
		{
			[InventoryType.PLAYER]: new Subject<PackedInventory | null>(null),
			[InventoryType.EQUIPMENT]: new Subject<PackedInventory | null>(null),
			[InventoryType.HOTBAR]: new Subject<PackedInventory | null>(null),
			[InventoryType.BACKPACK]: new Subject<PackedInventory | null>(null),
			[InventoryType.VEHICLE]: new Subject<PackedInventory | null>(null),
			[InventoryType.HOUSE]: new Subject<PackedInventory | null>(null),
			[InventoryType.EXCHANGE]: new Subject<PackedInventory | null>(null),
			[InventoryType.DROPPED]: new Subject<PackedInventory | null>(null),
		};

	private backpack = new Subject<boolean>(false);
	private isExchange = new Subject(false);
	private secondaryInventoryId = new Subject<string | null>(null);
	private secondaryInventoryType = new Subject<
		InventoryType.VEHICLE | InventoryType.HOUSE | InventoryType.DROPPED | null
	>(null);
	private secondatyTitle = new Subject<string | null>(null);

	private readonly unknownSubjects: Map<
		string,
		Subject<PackedInventory | null>
	> = new Map();
	private exchangeInventoriesIds = new Subject<{
		fromInventoryId: string;
		toInventoryId: string;
	}>({ fromInventoryId: '', toInventoryId: '' });
	private exchangeInventories = {
		fromInventoryType: new Subject<PackedInventory | null>(null),
		toInventoryType: new Subject<PackedInventory | null>(null),
	};
	private exchangeToMoneyAmount = new Subject<number>(0);
	private isSecondPlayerReady = new Subject<boolean>(false);
	private isPlayerReady = new Subject(false);
	private exchangeDelaySec = new Subject<number>(-1);
	private confirmationTimer: ReturnType<typeof setInterval> | null = null;
	private isExchangeCompleted = new Subject<boolean>(false);
	private otherPlayerInfo = new Subject<{
		nickname: string;
		level: number;
		serverPlayerId: number;
	}>({
		nickname: '',
		level: 0,
		serverPlayerId: 0,
	});
	private inventorySlotsCount = new Subject<{
		available: number;
		total: number;
	}>({ available: 0, total: 0 });
	private maxExchangeMoneyAmount = new Subject<number>(Number.MAX_SAFE_INTEGER);

	private notifyInitializedTypes = new Set<InventoryType>();
	private notifyLastKnownCounts = new Map<string, number>();
	private notifyDebounceTimer: ReturnType<typeof setTimeout> | null = null;
	private notifySkipNextBackpackUpdate = false;

	constructor(
		private readonly players: IPlayerService,
		private readonly backend: BackendService,
		private readonly vars: VarsService,
		private readonly gameConfig: GameConfigService,
		private readonly rpc: IRpcService,
		private readonly notifications: INotificationsService,
	) {
		this.backend.addEventHandler(
			'inventories:update',
			this.handleUpdate.bind(this),
		);
		this.vars
			.get('inventory:secondaryInventory', { id: null, type: null, title: '' })
			.subscribe(({ id, type, title }) => {
				this.clearInventories();
				this.secondatyTitle.next(title);
				this.secondaryInventoryId.next(id);
				this.secondaryInventoryType.next(type);
				if (id && type) {
					const inventory = this.unknownSubjects.get(id)?.getValue() ?? null;
					this.handleUpdate({ inventoryId: id, type, inventory });
				}
			});

		this.backend.addEventHandler(
			'inventories:exchangeStarted',
			(data: {
				fromInventoryId: string;
				toInventoryId: string;
				nickname: string;
				level: number;
				serverPlayerId: number;
			}) => {
				const {
					fromInventoryId,
					toInventoryId,
					nickname,
					level,
					serverPlayerId,
				} = data;
				this.resetExchange();
				this.exchangeInventoriesIds.next({ fromInventoryId, toInventoryId });
				this.exchangeInventories.fromInventoryType.next(
					this.unknownSubjects.get(fromInventoryId)?.getValue() ?? null,
				);
				this.exchangeInventories.toInventoryType.next(
					this.unknownSubjects.get(toInventoryId)?.getValue() ?? null,
				);
				this.otherPlayerInfo.next({ nickname, level, serverPlayerId });
			},
		);

		this.vars.get('inventory:isExchange', false).subscribe((isExchange) => {
			this.isExchange.next(isExchange);
			if (!isExchange) this.resetExchange();
		});

		this.backend.addEventHandler(
			'inventories:setIsPlayerReady',
			({ isReady }: { isReady: boolean }) => {
				this.isPlayerReady.next(isReady);
			},
		);
		this.backend.addEventHandler(
			'inventories:exchangeConfirmation',
			({ endsAt, serverNow }: InventoryExchangeConfirmation) => {
				if (this.confirmationTimer !== null)
					clearInterval(this.confirmationTimer);
				this.confirmationTimer = null;
				if (endsAt === null) {
					this.exchangeDelaySec.next(-1);
					return;
				}
				const localEndsAt = Date.now() + Math.max(0, endsAt - serverNow);
				const update = () => {
					const remaining = Math.max(
						0,
						Math.ceil((localEndsAt - Date.now()) / 1000),
					);
					this.exchangeDelaySec.next(remaining);
					if (remaining === 0 && this.confirmationTimer !== null) {
						clearInterval(this.confirmationTimer);
						this.confirmationTimer = null;
					}
				};
				update();
				if (localEndsAt > Date.now())
					this.confirmationTimer = setInterval(update, 250);
			},
		);

		this.backend.addEventHandler(
			'inventories:exchangeMoneyAmount',
			({ amount }: { amount: number }) => {
				this.exchangeToMoneyAmount.next(amount);
			},
		);

		this.backend.addEventHandler(
			'inventories:isSecondPlayerReady',
			({ isReady }: { isReady: boolean }) => {
				this.isSecondPlayerReady.next(isReady);
			},
		);

		this.backend.addEventHandler(
			'inventories:exchangeFinished',
			({ isCompleted }: { isCompleted: boolean }) => {
				this.isExchangeCompleted.next(isCompleted);
			},
		);

		this.gameConfig?.getConfigs().subscribe((config) => {
			if (config) {
				const maxExchangeMoneyAmount = config.inventory?.exchangeMaxMoneyAmount;
				this.maxExchangeMoneyAmount.next(
					maxExchangeMoneyAmount ?? Number.MAX_SAFE_INTEGER,
				);

				this.inventoryItems.next(config.inventory?.items ?? []);
			}
		});

		const playerInventorySubj = this.inventories[InventoryType.PLAYER];
		const backpackInventorySubj = this.inventories[InventoryType.BACKPACK];

		const updateInventorySlots = () => {
			const playerInventory = playerInventorySubj.getValue();
			const backpackInventory = backpackInventorySubj.getValue();

			let availableSlotsCount = 0;
			availableSlotsCount +=
				playerInventory?.items.filter((e) => e === null).length ?? 0;
			availableSlotsCount +=
				backpackInventory?.items.filter((e) => e === null).length ?? 0;

			let totalSlotsCount = 0;
			totalSlotsCount += playerInventory?.capacity ?? 0;
			totalSlotsCount += backpackInventory?.capacity ?? 0;

			this.inventorySlotsCount.next({
				available: availableSlotsCount,
				total: totalSlotsCount,
			});
		};

		playerInventorySubj.subscribe(() => updateInventorySlots());
		backpackInventorySubj.subscribe(() => updateInventorySlots());

		this.rpc.register<{ slot: number }>(
			'inventory:useHotbarSlot',
			async ({ slot }) => {
				await this.handleHotbarSlotUse(slot);
			},
		);
		this.rpc.register('inventories:reset', this.resetInventory.bind(this));
		this.players.getCharacterId().subscribe(() => this.resetInventory());
	}

	private resetInventory(): void {
		for (const inventory of Object.values(this.inventories))
			inventory.next(null);
		for (const inventory of this.unknownSubjects.values()) inventory.next(null);
		this.unknownSubjects.clear();
		const ids = { ...this.inventoryIds.getValue() };
		for (const type of Object.values(InventoryType)) ids[type] = '';
		this.inventoryIds.next(ids);
		this.backpack.next(false);
		this.secondaryInventoryId.next(null);
		this.secondaryInventoryType.next(null);
		this.secondatyTitle.next(null);
		this.isExchange.next(false);
		this.resetExchange();
		if (this.notifyDebounceTimer) clearTimeout(this.notifyDebounceTimer);
		this.notifyDebounceTimer = null;
		this.notifyInitializedTypes.clear();
		this.notifyLastKnownCounts.clear();
		this.notifySkipNextBackpackUpdate = false;
	}

	private handleUpdate(payload: {
		inventoryId?: string;
		type: InventoryType;
		inventory: PackedInventory | null;
	}) {
		const { type, inventory } = payload;
		const inventoryId = payload.inventoryId ?? inventory?.id;
		if (!inventoryId) return;
		if (!this.unknownSubjects.has(inventoryId))
			this.unknownSubjects.set(
				inventoryId,
				new Subject<PackedInventory | null>(null),
			);
		this.unknownSubjects.get(inventoryId)!.next(inventory);

		if (
			type === InventoryType.EQUIPMENT ||
			type === InventoryType.HOTBAR ||
			type === InventoryType.PLAYER
		) {
			if (
				inventory
					? inventory.ownerId !== this.players.getCharacterId().getValue()
					: this.inventoryIds.getValue()[type] !== inventoryId
			)
				return;
			const previousInventory = this.inventories[type].getValue();
			const prevWasNull = previousInventory === null;
			this.inventories[type].next(inventory);
			this.inventoryIds.next({
				...this.inventoryIds.getValue(),
				[type]: inventory?.id ?? '',
			});

			this.scheduleNotifyCheck(
				type,
				prevWasNull,
				previousInventory?.id !== inventory?.id,
			);
			return;
		}

		if (type === InventoryType.BACKPACK) {
			if (
				inventory
					? inventory.ownerId !== this.players.getCharacterId().getValue()
					: this.inventoryIds.getValue()[type] !== inventoryId
			)
				return;
			if (inventory) {
				const previousInventory =
					this.inventories[InventoryType.BACKPACK].getValue();
				const prevWasNull = previousInventory === null;
				const skipNotify =
					this.notifySkipNextBackpackUpdate ||
					previousInventory?.id !== inventory.id;
				this.notifySkipNextBackpackUpdate = false;
				this.inventories[InventoryType.BACKPACK].next(inventory);
				this.inventoryIds.next({
					...this.inventoryIds.getValue(),
					[InventoryType.BACKPACK]: inventory?.id ?? '',
				});
				this.backpack.next(true);

				this.scheduleNotifyCheck(
					InventoryType.BACKPACK,
					prevWasNull,
					skipNotify,
				);
				return;
			}

			this.inventories[InventoryType.BACKPACK].next(null);
			this.inventoryIds.next({
				...this.inventoryIds.getValue(),
				[InventoryType.BACKPACK]: '',
			});
			this.backpack.next(false);

			this.silentlyResyncNotifySnapshot();
			return;
		}

		if (type === InventoryType.EXCHANGE) {
			if (this.isPlayerReady.getValue() && this.isSecondPlayerReady.getValue())
				return;
			if (!inventory) {
				if (
					this.exchangeInventories.fromInventoryType.getValue()?.id ===
					inventoryId
				)
					this.exchangeInventories.fromInventoryType.next(null);
				if (
					this.exchangeInventories.toInventoryType.getValue()?.id ===
					inventoryId
				)
					this.exchangeInventories.toInventoryType.next(null);
				return;
			}

			const { fromInventoryId, toInventoryId } =
				this.exchangeInventoriesIds.getValue();
			if (inventory.id === fromInventoryId) {
				this.exchangeInventories.fromInventoryType.next(inventory);
			} else if (inventory.id === toInventoryId) {
				this.exchangeInventories.toInventoryType.next(inventory);
			}
		}

		if (
			type === InventoryType.VEHICLE ||
			type === InventoryType.HOUSE ||
			type === InventoryType.DROPPED
		) {
			if (
				this.secondaryInventoryId.getValue() !== inventoryId ||
				this.secondaryInventoryType.getValue() !== type
			)
				return;
			this.inventoryIds.next({
				...this.inventoryIds.getValue(),
				[type]: inventoryId,
			});
			this.inventories[type].next(inventory);

			return;
		}
	}

	private resetExchange(): void {
		if (this.confirmationTimer !== null) clearInterval(this.confirmationTimer);
		this.confirmationTimer = null;
		this.exchangeDelaySec.next(-1);
		this.isPlayerReady.next(false);
		this.isSecondPlayerReady.next(false);
		this.isExchangeCompleted.next(false);
		this.exchangeToMoneyAmount.next(0);
		this.exchangeInventoriesIds.next({
			fromInventoryId: '',
			toInventoryId: '',
		});
		this.exchangeInventories.fromInventoryType.next(null);
		this.exchangeInventories.toInventoryType.next(null);
	}

	private isNotifyReady(): boolean {
		return (
			this.notifyInitializedTypes.has(InventoryType.PLAYER) &&
			this.notifyInitializedTypes.has(InventoryType.HOTBAR) &&
			this.notifyInitializedTypes.has(InventoryType.EQUIPMENT)
		);
	}

	private syncNotifyInitializedTypes(): void {
		for (const type of PLAYER_INVENTORIES_FOR_PICKUP_NOTIFY) {
			if (this.inventories[type].getValue() !== null) {
				this.notifyInitializedTypes.add(type);
			}
		}
	}

	private ensureNotifyBaseline(): void {
		this.syncNotifyInitializedTypes();
		if (!this.isNotifyReady()) {
			return;
		}
		if (this.notifyLastKnownCounts.size > 0) {
			return;
		}
		this.notifyLastKnownCounts = this.getCombinedCatalogCounts();
	}

	private scheduleNotifyCheck(
		type: InventoryType,
		prevWasNull: boolean,
		skipNotify = false,
	): void {
		this.ensureNotifyBaseline();

		if (skipNotify) {
			this.notifyInitializedTypes.add(type);
			if (this.isNotifyReady()) {
				this.silentlyResyncNotifySnapshot();
			}
			return;
		}

		if (prevWasNull) {
			this.notifyInitializedTypes.add(type);
			if (this.isNotifyReady()) {
				this.silentlyResyncNotifySnapshot();
			}
			return;
		}

		if (!this.isNotifyReady()) {
			return;
		}
		if (this.notifyLastKnownCounts.size === 0) {
			this.notifyLastKnownCounts = this.getCombinedCatalogCounts();
			return;
		}

		if (this.notifyDebounceTimer !== null) {
			clearTimeout(this.notifyDebounceTimer);
		}
		this.notifyDebounceTimer = setTimeout(() => {
			this.notifyDebounceTimer = null;
			this.runNotifyCheck();
		}, 50);
	}

	private runNotifyCheck(): void {
		const after = this.getCombinedCatalogCounts();
		const positiveDeltas: { catalogId: string; delta: number }[] = [];

		for (const [catalogId, afterCount] of after) {
			const beforeCount = this.notifyLastKnownCounts.get(catalogId) ?? 0;
			const delta = afterCount - beforeCount;
			if (delta > 0) {
				positiveDeltas.push({ catalogId, delta });
			}
		}

		this.notifyLastKnownCounts = after;

		if (positiveDeltas.length === 0) {
			return;
		}
		if (positiveDeltas.length > PICKUP_BULK_CATALOG_IDS_THRESHOLD) {
			return;
		}

		for (const { catalogId, delta } of positiveDeltas) {
			const info = this.getItemInfoById(catalogId);
			const name =
				this.getItemNameById(catalogId) ??
				info?.subName ??
				info?.name ??
				catalogId;
			this.notifications.showCounter(name, delta, catalogId);
		}
	}

	private silentlyResyncNotifySnapshot(): void {
		if (this.notifyDebounceTimer !== null) {
			clearTimeout(this.notifyDebounceTimer);
			this.notifyDebounceTimer = null;
		}
		this.ensureNotifyBaseline();
		this.notifyLastKnownCounts = this.getCombinedCatalogCounts();
	}

	private getCatalogItemIdFromStackId(stackId: string): string {
		if (!stackId) {
			return '';
		}
		if (stackId.includes('-equip-')) {
			return stackId.split('-equip-')[0];
		}
		if (stackId.includes('-split-')) {
			return stackId.split('-split-')[0];
		}
		return stackId;
	}

	private aggregateInventoryCatalogCounts(
		inv: PackedInventory | null,
	): Map<string, number> {
		const map = new Map<string, number>();
		if (!inv?.items?.length) {
			return map;
		}
		for (const stack of inv.items) {
			if (!stack) {
				continue;
			}
			const catalogId = this.getCatalogItemIdFromStackId(stack.id);
			if (!catalogId) {
				continue;
			}
			map.set(catalogId, (map.get(catalogId) ?? 0) + stack.count);
		}
		return map;
	}

	private getCombinedCatalogCounts(): Map<string, number> {
		const combined = new Map<string, number>();
		for (const invType of PLAYER_INVENTORIES_FOR_PICKUP_NOTIFY) {
			const part = this.aggregateInventoryCatalogCounts(
				this.inventories[invType].getValue(),
			);
			for (const [catalogId, count] of part) {
				combined.set(catalogId, (combined.get(catalogId) ?? 0) + count);
			}
		}
		return combined;
	}

	private async handleHotbarSlotUse(slot: number): Promise<void> {
		const hotbarInventoryId =
			this.inventoryIds.getValue()[InventoryType.HOTBAR];
		if (!hotbarInventoryId) {
			return;
		}

		const hotbarInventory = this.inventories[InventoryType.HOTBAR].getValue();
		if (!hotbarInventory || !hotbarInventory.items?.length) {
			return;
		}

		const itemSlot = slot - 1;
		if (itemSlot < 0 || itemSlot >= hotbarInventory.capacity) {
			return;
		}

		const stack = hotbarInventory.items[itemSlot];
		if (!stack) {
			return;
		}

		const action = this.getHotbarItemAction(stack.id, itemSlot);
		if (!action) {
			return;
		}

		await this.performItemAction(
			hotbarInventoryId,
			stack.id,
			stack.count,
			stack.data,
			itemSlot,
			action,
		).catch(() => undefined);
	}

	private getHotbarItemAction(
		itemStackId: string,
		itemSlot: number,
	): ItemAction | null {
		const itemInfo = this.getItemInfoById(itemStackId);
		const baseActions = itemInfo?.availableActions ?? [];

		if (!baseActions.length) {
			return null;
		}

		const activeItems = this.vars
			.get<HeldItem[]>('inventories:activeItems', [])
			.getValue();

		const isInHands = activeItems.some(
			(item) =>
				item.inventoryType === InventoryType.HOTBAR &&
				item.itemSlot === itemSlot,
		);

		if (baseActions.includes(ItemAction.GIVE_HANDS)) {
			if (isInHands) {
				return ItemAction.TAKE_HANDS;
			}

			return ItemAction.GIVE_HANDS;
		}

		const equipmentInventory =
			this.inventories[InventoryType.EQUIPMENT].getValue();
		const isEquipped = equipmentInventory?.items.some(
			(stack) => stack && stack.id === itemStackId,
		);

		if (
			baseActions.includes(ItemAction.EQUIP) ||
			baseActions.includes(ItemAction.UNEQUIP)
		) {
			if (isEquipped) {
				return ItemAction.UNEQUIP;
			}

			return ItemAction.EQUIP;
		}

		if (baseActions.includes(ItemAction.USE)) {
			return ItemAction.USE;
		}

		return null;
	}

	public getInventoriesId(): IObservable<Record<InventoryType, string>> {
		return this.inventoryIds;
	}

	public async requestInventory(id: string): Promise<void> {
		const inventory = await this.getInventoryById(id);
		if (!inventory || !inventory.type) {
			return;
		}

		if (inventory.type === InventoryType.BACKPACK) {
			const currentBackpackId =
				this.inventories[InventoryType.BACKPACK].getValue()?.id;
			this.notifySkipNextBackpackUpdate = currentBackpackId !== inventory.id;
		}

		this.handleUpdate({
			inventoryId: inventory.id,
			type: inventory.type,
			inventory,
		});

		if (inventory.type === InventoryType.EQUIPMENT) {
			let backpackInventoryId: string | null = null;
			inventory.items.find((item) => {
				const data = item?.data;
				if (data && data.backpack?.inventoryId) {
					backpackInventoryId = data.backpack.inventoryId;
				}
			});
			if (backpackInventoryId) {
				this.inventoryIds.next({
					...this.inventoryIds.getValue(),
					[InventoryType.BACKPACK]: backpackInventoryId,
				});
				await this.requestInventory(backpackInventoryId);
				return;
			}
			this.inventoryIds.next({
				...this.inventoryIds.getValue(),
				[InventoryType.BACKPACK]: '',
			});
			this.backpack.next(false);
		}

		if (inventory.type === InventoryType.BACKPACK) {
			this.backpack.next(true);
		}
	}

	public getSecondaryInventoryId(): IObservable<string | null> {
		return this.secondaryInventoryId;
	}

	public getSecondaryInventoryType(): IObservable<
		InventoryType.VEHICLE | InventoryType.HOUSE | InventoryType.DROPPED | null
	> {
		return this.secondaryInventoryType;
	}

	public getSecondaryInventoryTitle(): IObservable<string | null> {
		return this.secondatyTitle;
	}

	public getIsExchange(): IObservable<boolean> {
		return this.isExchange;
	}

	public getMaxExchangeMoneyAmount(): IObservable<number> {
		return this.maxExchangeMoneyAmount;
	}

	public getInventory(id: string): IObservable<PackedInventory | null> {
		if (!this.unknownSubjects.has(id)) {
			this.unknownSubjects.set(id, new Subject<PackedInventory | null>(null));
		}
		return this.unknownSubjects.get(id)!;
	}

	public getFromExchangeInventory(): IObservable<PackedInventory | null> {
		return this.exchangeInventories.fromInventoryType;
	}

	public getToExchangeInventory(): IObservable<PackedInventory | null> {
		return this.exchangeInventories.toInventoryType;
	}

	private async invoke<T>(method: string, payload?: unknown): Promise<T> {
		const characterId = this.players.getCharacterId().getValue();
		try {
			return await (method === 'inventories:closeExchange'
				? this.rpc.invokeClient<T>(method, payload)
				: this.rpc.invokeServer<T>(method, payload));
		} catch (error) {
			const clientError =
				error instanceof ClientError
					? error
					: Object.assign(new ClientError(ClientErrorCode.InternalError), {
							cause: error,
					  });
			if (
				!(error instanceof ClientError) ||
				(error.getCode() === ClientErrorCode.InternalError &&
					[
						'timeout',
						'payload_too_large',
						'method_not_found',
						'method_not_allowed',
					].includes(String(error.getParams()?.reason)))
			) {
				console.error(
					{
						...Object.fromEntries(
							Object.entries(payload ?? {}).filter(
								([key, value]) =>
									[
										'inventoryId',
										'inventoryIdFrom',
										'inventoryIdTo',
										'itemId',
										'itemCount',
										'itemSlot',
										'itemSlotFrom',
										'itemSlotTo',
										'inventorySlotFrom',
										'inventorySlotTo',
									].includes(key) &&
									(typeof value === 'string' || typeof value === 'number'),
							),
						),
						operation: method,
						characterId,
					},
					error,
				);
			}
			// View handlers consume the rejection after their success continuations; presentation belongs here.
			this.notifications.showError(
				new ClientError(clientError.getCode()).message,
			);
			throw clientError;
		}
	}

	public async getInventoryById(id: string): Promise<PackedInventory | null> {
		const inventory = await this.invoke<PackedInventory | null>(
			'inventories:getById',
			{ inventoryId: id },
		);
		if (!inventory || !inventory.type) {
			return null;
		}
		return inventory;
	}

	public async moveItem(
		inventoryId: string,
		itemId: string,
		itemCount: number,
		itemData: ItemData | undefined,
		itemSlotFrom: number,
		itemSlotTo: number,
	): Promise<void> {
		await this.invoke('inventories:moveItem', {
			inventoryId,
			itemId,
			itemCount,
			itemData,
			itemSlotFrom,
			itemSlotTo,
		});
	}

	public async transferItem(
		itemId: string,
		itemCount: number,
		itemData: ItemData | undefined,
		inventoryIdFrom: string,
		inventorySlotFrom: number,
		inventoryIdTo: string,
		inventorySlotTo?: number,
	): Promise<void> {
		const payload: any = {
			itemId,
			itemCount,
			itemData,
			inventoryIdFrom,
			inventorySlotFrom,
			inventoryIdTo,
		};
		if (inventorySlotTo !== undefined) {
			payload.inventorySlotTo = inventorySlotTo;
		}

		await this.invoke('inventories:transferItem', payload);
	}

	public async performItemAction(
		inventoryId: string,
		itemId: string,
		itemCount: number,
		itemData: ItemData | undefined,
		itemSlot: number,
		action: ItemAction,
	): Promise<void> {
		await this.invoke('inventories:performItemAction', {
			inventoryId,
			itemId,
			itemCount,
			itemSlot,
			itemData,
			action,
		});
	}

	public hasBackpack(): IObservable<boolean> {
		return this.backpack;
	}

	public getItemInfoById(id: string): Partial<Item> | null {
		const config = this.gameConfig
			?.getConfigs()
			.getValue() as IClientConfigs | null;
		const baseId = id
			? id.includes('-equip-')
				? id.split('-equip-')[0]
				: id.includes('-split-')
				? id.split('-split-')[0]
				: id
			: '';
		const item = config?.inventory?.items.find((e) => e.id === baseId);
		if (!item) return null;

		const availableActions = [] as ItemAction[];
		if (item.isUsable) {
			availableActions.push(ItemAction.USE);
		}
		if (item.isClothes || item.isBackpack) {
			availableActions.push(ItemAction.EQUIP);
		}
		if (item.isDroppable) {
			availableActions.push(ItemAction.DROP);
		}
		if (item.isHoldable) {
			availableActions.push(ItemAction.GIVE_HANDS);
		}

		const data: ItemData = {};
		if (item.isClothes && item.clothesType) {
			data.clothes = { type: item.clothesType };
		}

		return {
			id,
			itemId: item.id,
			name: item.type,
			subName: item.name,
			hasUsageLimit: !!item?.maxDurability,
			usageLimit: item?.maxDurability ?? undefined,
			availableActions,
			type: item.type,
			stackable: item.isStackable,
			slots: item.isBackpack ? item.slotsCount ?? undefined : undefined,
			data,
		} as Partial<Item>;
	}

	public getInventoryByType(
		type: InventoryType,
	): IObservable<PackedInventory | null> {
		return this.inventories[type];
	}

	public getExchangeMoneyAmount(): IObservable<number> {
		return this.exchangeToMoneyAmount;
	}

	public async setExchangeMoneyAmount(amount: number): Promise<number> {
		return this.invoke<number>('inventories:setExchangeMoneyAmount', {
			amount,
		});
	}

	public async setIsPlayerReady(isReady: boolean): Promise<void> {
		await this.invoke('inventories:setIsPlayerReady', { isReady });
	}

	public getIsPlayerReady(): IObservable<boolean> {
		return this.isPlayerReady;
	}

	public getIsSecondPlayerReady(): IObservable<boolean> {
		return this.isSecondPlayerReady;
	}

	public getExchangeDelaySec(): IObservable<number> {
		return this.exchangeDelaySec;
	}

	public async setIsPlayerConfirmed(isConfirmed: boolean): Promise<void> {
		return await this.invoke('inventories:setIsPlayerConfirmed', {
			isConfirmed,
		});
	}

	public getIsExchangeCompleted(): IObservable<boolean> {
		return this.isExchangeCompleted;
	}

	public async cancelExchange(): Promise<void> {
		return await this.invoke('inventories:cancelExchange');
	}

	public async closeExchange(): Promise<void> {
		return await this.invoke('inventories:closeExchange');
	}

	public getOtherPlayerInfo(): IObservable<{
		nickname: string;
		level: number;
		serverPlayerId: number;
	}> {
		return this.otherPlayerInfo;
	}

	public clearInventories(): void {
		this.inventoryIds.next({
			...this.inventoryIds.getValue(),
			[InventoryType.HOUSE]: '',
			[InventoryType.VEHICLE]: '',
			[InventoryType.DROPPED]: '',
		});
		this.inventories[InventoryType.HOUSE].next(null);
		this.inventories[InventoryType.VEHICLE].next(null);
		this.inventories[InventoryType.DROPPED].next(null);
		this.secondaryInventoryId.next(null);
		this.secondaryInventoryType.next(null);
		this.secondatyTitle.next(null);
	}

	public getInventorySlotsCount(): IObservable<{
		available: number;
		total: number;
	}> {
		return this.inventorySlotsCount;
	}

	public getActiveItems(): IObservable<HeldItem[]> {
		return this.vars.get<HeldItem[]>('inventories:activeItems', []);
	}

	public getItemNameById(itemId: string): string | null {
		const itemConfigs = this.gameConfig.configs?.inventory?.items;
		return itemConfigs?.find((e) => e.id === itemId)?.name ?? null;
	}

	/**
	 * Returns observable for all available inventory items from config
	 */
	public getInventoryItems(): IObservable<
		IClientConfigs['inventory']['items']
	> {
		return this.inventoryItems;
	}

	public async exitMenu(): Promise<void> {
		return await this.rpc.invokeClient('inventory:exitMenu');
	}
}
